/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  Linkedin,
  Twitter,
  Instagram,
  MessageSquare,
  User,
  User2,
  X,
  CrossIcon,
  CircleAlert,
} from "lucide-react";
import MainLayout from "../../shared/components/MainLayout";
import Loader from "../../shared/components/loader/Loader";
import apiClient from "../../store/shared/api/apiClient";
import { ApiErrorHandler } from "../../../errorHandler";
import axios, { AxiosResponse } from "axios";
import { ErroObj } from "../../shared/interfaces/model/errorObj.interface";
import SuccessModal from "../multiDetailsTenant/components/SuccessModal";
import ErorrModal from "../multiDetailsTenant/components/ErorrModal";
import { getJWTDecoded } from "../../../utils/userUtils";

interface IssuePopupProps {
  show: boolean;
  onClose: () => void;
  onConfirm: (title: string, description: string) => void;
}

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const IssuePopup: React.FC<IssuePopupProps> = ({
  show,
  onClose,
  onConfirm,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (show) {
      setTitle("");
      setDescription("");
    }
  }, [show]);

  if (!show) return null;

  const handleSubmit = () => {
    onConfirm(title, description);
    onClose(); // Chiude il popup dopo l'invio
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Report an Issue</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter issue title"
          />
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            rows={4}
            placeholder="Enter issue description"
          ></textarea>
        </div>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-blue-700"
          >
            Report
          </button>
        </div>
      </div>
    </div>
  );
};

const sendContactRequest = async (contactData: {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}) => {
  const object = {
    contactUs: true,
    ...contactData,
  };

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const URL = `${baseUrl}/nexum-saas/sendInvoiceEmail`;

  return apiClient
    .post(URL, object)
    .then((response: AxiosResponse) => response)
    .catch((e: ErroObj) => {
      ApiErrorHandler(
        e.message,
        e.config.url,
        JSON.stringify(e.config.headers),
        e.config.method
      );
    });
};

const ContactPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    message?: string;
  } | null>(null);

  const validateForm = () => {
    const newError = { ...error };
    if (formData.firstName === "")
      newError.firstName = "First name is required";
    if (formData.lastName === "") newError.lastName = "Last name is required";
    if (formData.email === "") newError.email = "Email is required";
    if (formData.message === "") newError.message = "Message is required";
    setError(newError);
  };

  const validateFields = () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.message
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = (e: any) => {
    validateForm();
    if (!validateFields()) return;
    e.preventDefault();
    sendContactRequest(formData)
      .then((res) => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
        });
        setShowSuccessModal(true);
      })
      .catch((e) => {
        setShowErrorModal(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleReportIssue = (title: string, description: string) => {
    const isTenant = window.localStorage.getItem("isTenantUser");
    const emailTenant = window.localStorage.getItem("userEmail");
    const isUser = window.localStorage.getItem("isUser");
    const token = window.localStorage.getItem("token");
    let name = "N/A";
    if (token) {
      const decodedToken = getJWTDecoded(token);
      name = decodedToken.name;
    }
    setIsLoading(true);
    const projectKey = process.env.REACT_APP_JIRA_PROJECT_KEY;
    const label = process.env.REACT_APP_JIRA_LABEL;

    let kind = "Tenant";
    let valueName = "Ragione Sociale";
    if (isUser && isUser === "true") {
      kind = "User";
      valueName = "Name";
    } else if (isTenant && isTenant === "true") {
      kind = "TenantUser";
      valueName = "Name";
    }

    const fullDescription = `Opened by: \n${valueName}: ${name}\nEmail: ${emailTenant || "N/A"}\nKind: ${kind}\n\nMessage: \n${description}`;

    const jiraData = {
      fields: {
        project: {
          key: projectKey,
        },
        summary: title,
        description: {
          type: "doc",
          version: 1,
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: fullDescription,
                },
              ],
            },
          ],
        },
        issuetype: {
          name: "Task", // Adatta il tipo di issue se necessario (es: "Task", "Story")
        },
        labels: [label],
      },
    };

    axios
      .post(
        `${process.env.REACT_APP_API_BASE_URL}/nexum-saas/create-jira-issue`,
        jiraData
      )
      .then((response: AxiosResponse) => {
        setShowSuccessModal(true);
        setShowPopup(false);
      })
      .catch((error: any) => {
        setShowErrorModal(true);
        console.error("Error creating Jira issue:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // const email = process.env.REACT_APP_JIRA_EMAIL ?? 's.orlando@nexum-ai.com';
    // const apiKey = process.env.REACT_APP_JIRA_MAIN_KEY ?? '';
    // const authHeader = `Basic ${btoa(email + ':' + apiKey)}`;

    // axios.post('/rest/api/3/issue', jiraData, {
    //   headers: {
    //     'Authorization': authHeader,
    //     'Accept': 'application/json',
    //     'Content-Type': 'application/json'
    //   }
    // })
    //   .then((response: AxiosResponse) => {
    //     setShowSuccessModal(true);
    //     setShowPopup(false);
    //   })
    //   .catch((error: any) => {
    //     setShowErrorModal(true);
    //     console.error('Error creating Jira issue:', error);
    //   })
    //   .finally(() => {
    //     setIsLoading(false);
    //   });
  };

  return (
    <MainLayout>
      <div className="min-h-screen ">
        <Loader isLoading={isLoading} />
        {/* Header Section */}
        <motion.div className="text-center pt-16 pb-12 px-4" {...fadeIn}>
          <h1 className="text-5xl font-bold  text-[#0c1d46] mb-4">
            Get in Touch
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and
            we'll respond as soon as possible.
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              className="bg-white rounded-2xl shadow-xl p-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                Send us a Message
              </h2>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 flex-row">
                      <User /> First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1e3770] focus:border-transparent"
                    />
                    {error?.firstName && (
                      <p className="text-red-500 mt-1 text-sm flex items-center gap-2 flex-row">
                        <CircleAlert className="w-4 h-4" />
                        {error.firstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 flex-row">
                      <User2 /> Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1e3770] focus:border-transparent"
                    />
                    {error?.lastName && (
                      <p className="text-red-500 mt-1 text-sm flex items-center gap-2 flex-row">
                        <CircleAlert className="w-4 h-4" />
                        {error.lastName}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 flex-row">
                    <Mail /> Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1e3770] focus:border-transparent"
                  />
                  {error?.email && (
                    <p className="text-red-500 mt-1 text-sm flex items-center gap-2 flex-row">
                      <CircleAlert className="w-4 h-4" />
                      {error.email}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 flex-row">
                    <MessageSquare /> Message
                  </label>
                  <textarea
                    rows={4}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1e3770] focus:border-transparent"
                  ></textarea>
                  {error?.message && (
                    <p className="text-red-500 mt-1 text-sm flex items-center gap-2 flex-row">
                      <CircleAlert className="w-4 h-4" />
                      {error.message}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-[#012475] text-white py-3 px-6 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
                  onClick={handleSubmit}
                >
                  <Send className="w-5 h-5" />
                  <span>Send Message</span>
                </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <Phone className="w-8 h-8 text-[#0c1d46] mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Phone</h3>
                  <p className="text-gray-600">+39 347 777 77 77</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <Mail className="w-8 h-8 text-[#0c1d46] mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Email</h3>
                  <p className="text-gray-600">info@example.com</p>
                </div> */}

                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <MapPin className="w-8 h-8 text-[#0c1d46] mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Location
                  </h3>
                  <p className="text-gray-600">
                    Viale Citta’ D’Europa 681
                    <br />
                    00144 Roma (RM)
                  </p>
                </div>

                {/* <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <Clock className="w-8 h-8 text-[#0c1d46] mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Hours</h3>
                <p className="text-gray-600">Mon-Fri: 9:00 AM - 6:00 PM<br />Sat-Sun: Closed</p>
              </div> */}

                {/* Live Chat Card */}
                <motion.div
                  className="bg-gradient-to-r from-indigo-600 to-[#012475] p-8 rounded-xl text-white"
                  whileHover={{ scale: 1.02 }}
                >
                  <MessageSquare className="w-8 h-8 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Report Issue</h3>
                  <p className="mb-4">Get instant help from our support team</p>
                  <button
                    onClick={handleOpenPopup}
                    className="bg-white text-[#0c1d46] py-2 px-6 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Report
                  </button>
                </motion.div>

                <IssuePopup
                  show={showPopup}
                  onClose={handleClosePopup}
                  onConfirm={(title, description) =>
                    handleReportIssue(title, description)
                  }
                />
              </div>

              {/* Social Links */}
              <div className="flex justify-center space-x-6">
                <motion.a
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  className="bg-white p-3 rounded-full shadow-lg"
                >
                  <Linkedin className="w-6 h-6 text-[#0c1d46]" />
                </motion.a>
                <motion.a
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  className="bg-white p-3 rounded-full shadow-lg"
                >
                  <X className="w-6 h-6 text-[#0c1d46]" />
                </motion.a>
                <motion.a
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  className="bg-white p-3 rounded-full shadow-lg"
                >
                  <Instagram className="w-6 h-6 text-[#0c1d46]" />
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      {showSuccessModal && (
        <SuccessModal
          show={showSuccessModal}
          setShow={setShowSuccessModal}
          description="Message sent successfully"
        />
      )}
      {showErrorModal && (
        <ErorrModal
          show={showErrorModal}
          setShow={setShowErrorModal}
          emailError={"Please try again"}
        />
      )}
    </MainLayout>
  );
};

export default ContactPage;
